import React, { useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { addFaq, editFaq } from "services/faq";
import { StyledFaqDialog } from "./Faq.style";

const hasRichTextValue = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .trim().length > 0;

const FaqDlg = (props) => {
  const initFaq = {
    title: "",
    answer: "",
  };

  const { enqueueSnackbar } = useSnackbar();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: initFaq,
  });

  const { onSubmit } = props;
  const [faq, setFaq] = useState(initFaq);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const openDlg = (editFaq) => {
    const nextFaq = editFaq || initFaq;
    setFaq(nextFaq);
    setIsEditMode(Boolean(editFaq));
    reset({
      title: nextFaq.title || "",
      answer: nextFaq.answer || "",
    });
    setOpen(true);
  };

  const handleAction = async (data) => {
    // send request
    setLoading(true);
    const response = isEditMode
      ? await editFaq({ ...data, id: faq.id })
      : await addFaq(data);
    if (response.rtnCode === 200) {
      // handling success message
      enqueueSnackbar(isEditMode ? "FAQ Update" : "Create FAQ success", {
        variant: "success",
      });
    } else {
      // handling error message
      enqueueSnackbar(isEditMode ? "FAQ Update Fail" : "Create FAQ Fail", {
        variant: "warning",
      });
    }
    setLoading(false);
    onSubmit();
    handleClose();
  };

  const answerValue = watch("answer") || "";

  const dlg = (
    <StyledFaqDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <div className="dialog-title-group">
          <h2 className="dialog-title">{isEditMode ? "Edit FAQ" : "Add FAQ"}</h2>
          <p className="dialog-subtitle">
            Manage the question prompt and rich text answer content.
          </p>
        </div>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogContent>
          <div className="dialog-field-group">
            <TextField
              label="FAQ question"
              rows={3}
              multiline
              fullWidth
              size="small"
              variant="outlined"
              {...register("title", { required: true })}
              error={!!errors?.title}
              helperText={!!errors?.title ? "Question is required" : " "}
            />
            <FormControl>
              <input
                type="hidden"
                {...register("answer", {
                  validate: (value) =>
                    hasRichTextValue(value) || "Answer is required",
                })}
              />
              <ReactQuill
                theme="snow"
                value={answerValue}
                onChange={(val) => {
                  setValue("answer", val, { shouldValidate: true });
                }}
              />
              {!!errors?.answer && (
                <div className="error">{errors.answer.message}</div>
              )}
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            onClick={handleClose}
            variant="outlined"
            size="medium"
          >
            Cancel
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledFaqDialog>
  );

  return [openDlg, dlg, loading];
};

export default FaqDlg;
